/**
 * Persistent app sidebar.
 *
 * Lives under `components/layout` because every signed-in page renders
 * it via `AppShellLayout`. Self-contained: it computes its own active
 * link from the current route (React Router's `useLocation`) and only
 * needs the nav data from `nav-items`.
 *
 * Two fold mechanics:
 *   - GLOBAL fold (the panel-toggle at the top): collapses the whole menu
 *     to a narrow icon-only rail. Labels, section headers, counts and the
 *     user-chip meta hide; the icons stay. State is owned by
 *     `useSidebarCollapsed` and persisted across reloads.
 *   - SECTION fold: each labelled section header (e.g. "Viral Radar") is a
 *     button that collapses the items under it. Default expanded. Ignored
 *     while the rail is globally collapsed (labels are hidden anyway).
 *
 * Items flagged `soon: true` in `nav-items` are currently filtered out at
 * the data layer (`SHOW_SOON_NAV`); the render branch for them is kept so
 * flipping the flag restores them with no component change.
 */
import {
  Bell,
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretDown,
  CaretRight,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';

import { UserChip, type UserChipUser } from '@/components/layout/user-chip/user-chip';
import { Icon } from '@/components/ui/icon/icon';
import { paths } from '@/config/paths';

import styles from './app-sidebar.module.css';
import { NAV_SECTIONS } from './nav-items';

import type { NavKey } from './nav-items';

export interface AppSidebarProps {
  /** Override the auto-detected active key (useful for tests / Storybook). */
  activeKey?: NavKey;
  /** Signed-in user for the account chip pinned to the sidebar bottom.
   *  Omitted in tests/Storybook that only exercise navigation. */
  user?: UserChipUser | null;
  /** Sign-out handler for the account chip. Required alongside `user`. */
  onSignOut?: () => void;
  /** Setup progress for the persistent "Get Started" entry + ring. The
   *  entry is hidden when omitted or once done >= total (onboarding done). */
  setupProgress?: { done: number; total: number } | null;
  /** Whole-menu icon-rail state. When true the labels collapse to icons. */
  collapsed?: boolean;
  /** Toggles the icon rail. Owned by the shell so the grid column resizes. */
  onToggleCollapse?: () => void;
  /** Plan line for the account chip (e.g. "Creator (Trial) · 480 cr").
   *  Falls back to "Free Plan" when omitted. */
  planLabel?: string;
}

// Get Started ring geometry · r=9 → circumference 2πr ≈ 56.5.
const GS_RING_CIRCUMFERENCE = 2 * Math.PI * 9;

function resolveActiveKey(pathname: string): NavKey | null {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
        return item.key;
      }
    }
  }
  return null;
}

export function AppSidebar({
  activeKey,
  user,
  onSignOut,
  setupProgress,
  collapsed = false,
  onToggleCollapse,
  planLabel,
}: AppSidebarProps) {
  const location = useLocation();
  const resolvedKey = activeKey ?? resolveActiveKey(location.pathname);
  // Show the Get Started entry only while setup is unfinished. Narrowed to
  // a non-null local so the ring math below needs no non-null assertion.
  const gs = setupProgress && setupProgress.done < setupProgress.total ? setupProgress : null;
  const gsActive = location.pathname.startsWith(paths.gettingStarted);

  // Which labelled sections are folded shut. Empty = all expanded (default).
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set());
  const toggleSection = (label: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <aside
      className={[styles.sidebar, collapsed ? styles.sidebarCollapsed : '']
        .filter(Boolean)
        .join(' ')}
      aria-label="Primary navigation"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <div className={styles.topRow}>
        <Link className={styles.brand} to={paths.dashboard} aria-label="AvatarFactory home">
          <div className={styles.brandMark} aria-hidden="true">
            <img src="/brand/brand-mark.svg" alt="" />
          </div>{' '}
          <div className={styles.brandName}>
            Avatar<span>Factory</span>
          </div>
        </Link>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon icon={collapsed ? CaretDoubleRight : CaretDoubleLeft} size={16} />
        </button>
      </div>

      <nav className={styles.nav}>
        {gs ? (
          <Link
            to={paths.gettingStarted}
            data-key="getting-started"
            className={[styles.navItem, styles.getStarted, gsActive ? styles.navItemActive : '']
              .filter(Boolean)
              .join(' ')}
            aria-current={gsActive ? 'page' : undefined}
            title="Get Started"
          >
            <span className={styles.gsRing} aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  fill="none"
                  stroke="var(--surface-3)"
                  strokeWidth="3"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  fill="none"
                  stroke="var(--cyan)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={GS_RING_CIRCUMFERENCE.toFixed(1)}
                  strokeDashoffset={(GS_RING_CIRCUMFERENCE * (1 - gs.done / gs.total)).toFixed(1)}
                  transform="rotate(-90 12 12)"
                />
              </svg>
            </span>
            <span className={styles.navLabel}>Get Started</span>
            <span className={styles.gsCount}>
              {gs.done}/{gs.total}
            </span>
          </Link>
        ) : null}
        {NAV_SECTIONS.map((section, sectionIdx) => {
          const label = section.label;
          const sectionFolded = label != null && collapsedSections.has(label);
          // Globally collapsed → always show icons (the fold UI is hidden).
          const itemsVisible = collapsed || !sectionFolded;
          return (
            <div
              key={label ?? `section-${sectionIdx}`}
              className={styles.section}
              data-section={label ?? 'main'}
            >
              {label ? (
                <button
                  type="button"
                  className={styles.sectionLabel}
                  onClick={() => toggleSection(label)}
                  aria-expanded={!sectionFolded}
                >
                  <span className={styles.sectionLabelText}>{label}</span>
                  <Icon
                    icon={sectionFolded ? CaretRight : CaretDown}
                    size={12}
                    className={styles.sectionChevron}
                  />
                </button>
              ) : null}
              {itemsVisible
                ? section.items.map((item) => {
                    if (item.soon) {
                      // Soon items are not links · they signal "planned for after
                      // launch" so users keep the mental model of the full product
                      // shape but can't click through to a stub page.
                      return (
                        <span
                          key={item.key}
                          data-key={item.key}
                          className={[styles.navItem, styles.navItemSoon].join(' ')}
                          aria-disabled="true"
                          title="Coming soon · planned for after launch"
                        >
                          <span className={styles.navIcon}>{item.icon}</span>
                          <span className={styles.navLabel}>{item.label}</span>
                          <span className={styles.soonPill}>Soon</span>
                        </span>
                      );
                    }
                    if (item.external) {
                      // External items point to non-SPA URLs (e.g. server-rendered
                      // Flask pages). Use a regular anchor so the browser does a
                      // full navigation instead of React-Router's client-side
                      // routing (which would otherwise fall through to the SPA
                      // catch-all). Active state is decided by URL match too.
                      const isActive = location.pathname === item.to;
                      return (
                        <a
                          key={item.key}
                          href={item.to}
                          data-key={item.key}
                          className={[styles.navItem, isActive ? styles.navItemActive : '']
                            .filter(Boolean)
                            .join(' ')}
                          aria-current={isActive ? 'page' : undefined}
                          title={item.label}
                        >
                          <span className={styles.navIcon}>{item.icon}</span>
                          <span className={styles.navLabel}>{item.label}</span>
                        </a>
                      );
                    }
                    const isActive = resolvedKey === item.key;
                    return (
                      <Link
                        key={item.key}
                        to={item.to}
                        data-key={item.key}
                        className={[styles.navItem, isActive ? styles.navItemActive : '']
                          .filter(Boolean)
                          .join(' ')}
                        aria-current={isActive ? 'page' : undefined}
                        title={item.label}
                      >
                        <span className={styles.navIcon}>{item.icon}</span>
                        <span className={styles.navLabel}>{item.label}</span>
                      </Link>
                    );
                  })
                : null}
            </div>
          );
        })}
      </nav>

      <div className={styles.utility}>
        <button
          type="button"
          className={[styles.navItem, styles.navItemButton].join(' ')}
          aria-label="Notifications"
          title="Notifications"
        >
          <span className={styles.navIcon}>
            <Icon icon={Bell} />
          </span>
          <span className={styles.navLabel}>Notifications</span>
          <span className={styles.notifDot} aria-hidden="true" />
        </button>
      </div>

      {user && onSignOut ? (
        <div className={styles.footer}>
          <UserChip
            user={user}
            onSignOut={onSignOut}
            placement="sidebar"
            compact={collapsed}
            planLabel={planLabel}
          />
        </div>
      ) : null}
    </aside>
  );
}
